import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTokens } from '../../styles/tokens';
import { useRoom } from '../../hooks/useRoom';
import { useMaintenance } from '../../hooks/useMaintenance';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { Avatar } from '../common/Avatar';
import { Navbar } from '../common/Navbar';
import { useToast } from '../common/Toast';
import { getRandomAnimal } from '../../../domain/valueObjects/AnimalAvatars';

const Wrapper = styled.div`
  min-height: 100vh;
`;

const UserName = styled.span<{ color: string }>`
  font-size: 14px;
  color: ${p => p.color};
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 48px 24px;
`;

const PageTitle = styled.h1<{ color: string }>`
  font-size: 2rem;
  font-weight: 800;
  color: ${p => p.color};
  margin-bottom: 8px;
`;

const PageSub = styled.p<{ color: string }>`
  color: ${p => p.color};
  margin-bottom: 40px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const Card = styled.div<{ surface: string; border: string }>`
  background: ${p => p.surface};
  border: 1px solid ${p => p.border};
  border-radius: 16px;
  padding: 28px;
`;

const CardTitle = styled.h2<{ color: string }>`
  font-size: 18px;
  font-weight: 700;
  color: ${p => p.color};
  margin-bottom: 20px;
`;

const Label = styled.label<{ color: string }>`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.color};
  margin-bottom: 6px;
`;

const MaintenanceBanner = styled.div<{ tokens: any }>`
  background: ${p => p.tokens.warning ?? '#f59e0b'};
  color: #1f2937;
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const LobbyPage: React.FC = () => {
  const { user, signOut } = useAuth();
  const { theme } = useTheme();
  const { t } = useLanguage();
  const tok = getTokens(theme);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [roomName, setRoomName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [creating, setCreating] = useState(false);

  const { createRoom, joinRoom } = useRoom(null);
  const maintenanceActive = useMaintenance();

  const handleCreate = async () => {
    if (!user) return;
    if (maintenanceActive) { showToast(t.maintenanceCreateBlocked, 'error'); return; }
    if (!roomName.trim()) { showToast(t.errorRoomName, 'error'); return; }
    setCreating(true);
    try {
      const id = await createRoom(roomName.trim(), user.id, user.name, getRandomAnimal());
      showToast(t.roomCreatedMsg, 'success');
      navigate(`/room/${id}`);
    } catch (e: any) {
      showToast(e.message, 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleJoin = async () => {
    if (!user) return;
    const code = joinCode.trim();
    if (!code) { showToast(t.errorRoomCode, 'error'); return; }
    await joinRoom(code, user.id, user.name, getRandomAnimal(), false);
    navigate(`/room/${code}`);
  };

  if (!user) {
    navigate('/');
    return null;
  }

  return (
    <Wrapper>
      <Navbar
        tokens={tok}
        onLogoClick={() => navigate('/')}
        rightContent={
          <>
            <Avatar name={user.name} size={32} tokens={tok} />
            <UserName color={tok.textMuted}>{user.name}</UserName>
            <Button tokens={tok} variant="ghost" size="sm" onClick={signOut}>{t.signOut}</Button>
          </>
        }
      />

      <Content>
        <PageTitle color={tok.text}>{t.lobbyTitle}</PageTitle>
        <PageSub color={tok.textMuted}>{t.lobbySubtitle}</PageSub>

        {maintenanceActive && (
          <MaintenanceBanner tokens={tok}>
            🧹 {t.maintenanceMsg}
          </MaintenanceBanner>
        )}

        <Grid>
          <Card surface={tok.bgSurface} border={tok.border}>
            <CardTitle color={tok.text}>{t.newRoomCardTitle}</CardTitle>
            <Label color={tok.textMuted}>{t.roomNameLabel}</Label>
            <Input
              tokens={tok}
              placeholder={t.roomNamePlaceholder}
              value={roomName}
              onChange={e => setRoomName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
              style={{ marginBottom: 16 }}
            />
            <Button tokens={tok} fullWidth disabled={creating || maintenanceActive} onClick={handleCreate}>
              {creating ? t.creatingBtn : t.createRoomBtn}
            </Button>
          </Card>

          <Card surface={tok.bgSurface} border={tok.border}>
            <CardTitle color={tok.text}>{t.joinRoomCardTitle}</CardTitle>
            <Label color={tok.textMuted}>{t.roomCodeLabel}</Label>
            <Input
              tokens={tok}
              placeholder={t.roomCodePlaceholder}
              value={joinCode}
              onChange={e => setJoinCode(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleJoin()}
              style={{ marginBottom: 16 }}
            />
            <Button tokens={tok} variant="secondary" fullWidth onClick={handleJoin}>
              {t.joinBtn}
            </Button>
          </Card>
        </Grid>
      </Content>
    </Wrapper>
  );
};
